/*
 * account-recover.js — Migrated-account self-serve password setup (dual-fire).
 *
 * On the login page's "Forgot password" (#recover) form, this fires a
 * fire-and-forget POST to the Cloudflare Worker /account-recover endpoint
 * ALONGSIDE Shopify's native recover_customer_password submission. The worker
 * emails DISABLED/INVITED (migrated) customers a branded "Set your password"
 * link; Shopify's native reset handles ENABLED accounts. The two paths are
 * mutually exclusive by account state, so at most one email is sent (no
 * double-email). The page shows Shopify's own generic recover_success
 * confirmation; this script NEVER reveals account state.
 *
 * keepalive:true is REQUIRED — the native recover form is a synchronous
 * full-page POST that navigates away; without keepalive the browser would
 * abort the in-flight worker fetch before it sends, and the migrated
 * customer's email would silently never go out. See
 * rules/migrated_account_activation.md.
 *
 * Mirrors theme/assets/contact-form-enrich.js (capture-phase intercept +
 * sentinel) so it's robust to Shopify's invisible hCaptcha (which calls
 * form.submit() directly, bypassing the submit event). UNLIKE that file, this
 * does NOT preventDefault — the native Shopify recover submit MUST proceed.
 *
 * Constraints (vanilla JS / IIFE, no deps): read email by id (#RecoverEmail);
 * no analytics/tracking tags; never display account state or the activation
 * URL; never block the native submit on the worker call.
 */
(function () {
  'use strict';

  var RECOVER_URL = 'https://downloads-api.nightingale.com/account-recover';

  function init() {
    var input = document.getElementById('RecoverEmail');
    var form = input && input.form;
    if (!form) return; // recover form not present on this page

    // Fire on the submit event (keyboard Enter) AND the button click (capture
    // phase — robust to hCaptcha calling form.submit() directly). A sentinel
    // ensures the worker ping fires at most once per submission. Neither path
    // calls preventDefault — the native Shopify recover submit MUST proceed.
    form.addEventListener('submit', function () { firePing(form, input); }, true);
    var btn = form.querySelector('button, input[type="submit"]');
    if (btn) {
      btn.addEventListener('click', function () { firePing(form, input); }, true);
    }
  }

  function firePing(form, input) {
    if (form.dataset.recoverPingFired === 'true') return; // already fired this submission
    form.dataset.recoverPingFired = 'true';

    var email = (input && typeof input.value === 'string')
      ? input.value.trim().toLowerCase()
      : '';
    if (!email) return; // let the native form handle the empty/invalid case

    try {
      fetch(RECOVER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email }),
        keepalive: true,   // survive the native form's full-page navigation
        credentials: 'omit',
      }).catch(function () {
        /* enumeration-safe no-op: the native submit still proceeds, so a
           worker/network failure is no worse than today. */
      });
    } catch (_e) {
      /* never block the native submit on a fetch construction error */
    }
  }

  // --- Login-form path: a failed native sign-in for a migrated (DISABLED)
  // account gets the SAME branded set-password email (/account-recover) plus an
  // on-page "check your inbox" notice, so a customer who tried to LOG IN (not
  // click "Forgot password") isn't left at a dead "incorrect password" error.
  // Enumeration-safe: the worker only emails DISABLED/INVITED accounts, no-ops
  // for ENABLED, and the notice copy is generic ("if you're returning...").
  // The per-email + per-IP rate limits in the worker bound abuse.
  var LOGIN_STASH_KEY = 'nc_login_attempt_email';

  function stashLoginEmail(input) {
    var email = (input && typeof input.value === 'string')
      ? input.value.trim().toLowerCase() : '';
    if (!email) return;
    try { window.sessionStorage.setItem(LOGIN_STASH_KEY, email); } catch (_e) {}
  }

  function initLogin() {
    var input = document.getElementById('CustomerEmail');
    var form = input && input.form;
    if (form) {
      // Stash the attempted email on submit (Enter) AND sign-in button click
      // (capture phase — robust to hCaptcha calling form.submit() directly).
      form.addEventListener('submit', function () { stashLoginEmail(input); }, true);
      var btn = form.querySelector('button, input[type="submit"]');
      if (btn) { btn.addEventListener('click', function () { stashLoginEmail(input); }, true); }
    }
    handleLoginFailure();
  }

  function handleLoginFailure() {
    // Fire ONLY when the server rendered a login-failure marker AND we captured
    // the attempted email on the prior submit (so we have an email to act on).
    if (!document.getElementById('nc-login-failed')) return;

    var email = '';
    try { email = window.sessionStorage.getItem(LOGIN_STASH_KEY) || ''; } catch (_e) {}
    try { window.sessionStorage.removeItem(LOGIN_STASH_KEY); } catch (_e) {}
    if (!email) return;

    var notice = document.getElementById('nc-migrated-login-notice');

    // Ask the worker whether this account actually needs a password (migrated /
    // first-time). Reveal the "check your inbox" notice ONLY when it does, so a
    // regular customer who just mistyped sees nothing extra. Fail-closed: any
    // network/parse error leaves the notice hidden.
    try {
      fetch(RECOVER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email }),
        credentials: 'omit',
      })
        .then(function (r) { return r && r.ok ? r.json() : null; })
        .then(function (data) {
          if (data && data.needs_password_setup === true && notice) {
            notice.hidden = false;
          }
        })
        .catch(function () { /* fail-closed: leave the notice hidden */ });
    } catch (_e) { /* never throw on a fetch construction error */ }
  }

  function boot() { init(); initLogin(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
